import { Component, OnInit, OnDestroy } from '@angular/core';
import { TableCompte } from '../../../../../data/schemas/table.schema';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AppService } from '../../../../../core/app.service';
import { UiTable } from '../../../../../components/ui/table/table.component';
import { ModalComponent } from '../../../../../components/modal/modal.component';
import { Input } from '../../../../../components/forms/input/input';
import { SelectComponent } from "../../../../../components/forms/select/select.component";
import { TypeCompteRes } from '../../../../../data/http/responses/typecompte.res';
import { TypeCompteService } from '../../../../../services/typecompte.service';
import { SelectOption } from '../../../../../data/schemas/selectoption.schema';
import { CompteService } from '../../../../../services/compte.service';
import { escapeRegExp } from '../../../../../utils/helpers';
import { Subscription, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { UiButtonComponent } from '../../../../../components/button/button.component';
import { loaderIntervantion } from '../../../../../components/ui/loaderIntervantion/loaderIntervantion.component';
import { ConfirmDialogComponent } from '../../../../../components/ui/Confirmdialog/Confirmdialog.component';

@Component({
  selector: 'app-compte',
  standalone: true,
  imports: [
    UiTable,
    ModalComponent,
    ReactiveFormsModule,
    Input,
    SelectComponent,
    UiButtonComponent,
    loaderIntervantion,
    ConfirmDialogComponent
  ],
  templateUrl: './compte.component.html',
  styleUrl: './compte.component.css'
})
export class ComptePage implements OnInit, OnDestroy {
  public tableEntity = TableCompte;
  public showAddEntityModal: boolean = false;
  public isAddParent: boolean = false;
  public entities: any[] = [];
  public isLoading: boolean = false;
  public isEditing: boolean = false;
  public editingEntity: any | null = null;
  public typeComptes: SelectOption[] = [];

  // Confirmation suppression
  public showDeleteConfirm: boolean = false;
  public selectedItem: any | null = null;
  public deleteLoading: boolean = false;

  entityForm!: FormGroup;
  private parentCodeForSubAccount: string = '';
  private codeValueSubscription: Subscription | undefined;

  constructor(
    private entityService: CompteService,
    private fb: FormBuilder,
    private appService: AppService,
    private typeCompteService: TypeCompteService
  ) {
    this.entityForm = this.fb.group({
      applicationId: this.appService.getAppId()!,
      code: ['', [Validators.required]],
      intituler: ['', [Validators.required]],
      typeCompte: ['', [Validators.required]],
      solde: [''],
      parentDisplay: [''],
      parentId: [''],
    });
  }

  ngOnInit(): void {
    this.loadEntities();
    this.loadTypeComptes().subscribe();
  }

  ngOnDestroy(): void {
    this.codeValueSubscription?.unsubscribe();
  }

  saveEntity(): void {
    if (this.entityForm.valid) {
      this.isLoading = true;
      const requestBody: any = {
        applicationId: this.appService.getAppId()!,
        intituler: this.entityForm.get('intituler')?.value,
        typeCompte: this.entityForm.get('typeCompte')?.value,
        solde: this.entityForm.get('solde')?.value,
        code: this.entityForm.get('code')?.value
      };

      const parentIdControl = this.entityForm.get('parentId');
      if (parentIdControl && parentIdControl.value) {
        requestBody.parent = parentIdControl.value;
      }

      if (this.isEditing && this.editingEntity) {
        this.entityService.update(this.editingEntity.id, requestBody).subscribe({
          next: () => { this.loadEntities(); this.closeAddEntityModal(); this.isLoading = false; },
          error: (e) => { console.error(e); this.isLoading = false; }
        });
      } else {
        this.entityService.create(requestBody).subscribe({
          next: () => { this.loadEntities(); this.closeAddEntityModal(); this.isLoading = false; },
          error: (e) => { console.error(e); this.isLoading = false; }
        });
      }
    } else {
      this.entityForm.markAllAsTouched();
    }
  }

  loadEntities(): void {
    this.isLoading = true;
    this.entityService.getAllByAppId(this.appService.getAppId()!).subscribe({
      next: (response: any) => { this.entities = response.data; this.isLoading = false; },
      error: (e) => { console.error(e); this.isLoading = false; }
    });
  }

  loadTypeComptes(): Observable<SelectOption[]> {
    if (this.typeComptes.length > 0) return of(this.typeComptes);
    return this.typeCompteService.getAllByAppId(this.appService.getAppId()!).pipe(
      map((response: any) => {
        this.typeComptes = response.data.map((item: TypeCompteRes) => ({
          value: item.libelle,
          label: item?.libelle
        }));
        return this.typeComptes;
      }),
      take(1)
    );
  }

  openAddEntityModal(isAddParentModal: boolean, item?: any): void {
    this.isEditing = false;
    this.editingEntity = null;
    this.isAddParent = isAddParentModal;
    this._resetForm();
    this._enableAllControls();

    if (this.isAddParent) {
      this.entityForm.get('parentDisplay')?.disable();
      this.entityForm.get('parentId')?.disable();
    } else {
      if (item && item.id && item.code) {
        this.parentCodeForSubAccount = item.code;
        this.entityForm.patchValue({ parentId: item.id, parentDisplay: item.code });

        const prefix = this.parentCodeForSubAccount + '_';
        this.entityForm.get('code')?.setValue(prefix);
        this.entityForm.get('parentDisplay')?.disable();
        this.entityForm.get('parentId')?.disable();

        this.codeValueSubscription = this.entityForm.get('code')!.valueChanges.subscribe(childCode => {
          if (childCode === null || childCode === undefined) {
            this.entityForm.get('code')?.patchValue(prefix, { emitEvent: false });
            return;
          }
          if (!childCode.startsWith(prefix)) {
            const rawSuffix = childCode.replace(
              new RegExp('^' + escapeRegExp(prefix) + '|^' + escapeRegExp(this.parentCodeForSubAccount) + '_', ''), ''
            );
            this.entityForm.get('code')?.patchValue(prefix + rawSuffix, { emitEvent: false });
          }
        });
      } else {
        console.warn('Item parent manquant pour le sous-compte.');
        this.closeAddEntityModal();
        return;
      }
    }
    this.showAddEntityModal = true;
  }

  closeAddEntityModal(): void {
    this.showAddEntityModal = false;
    this.isEditing = false;
    this.editingEntity = null;
    this.isAddParent = false;
    this.parentCodeForSubAccount = '';
    this.codeValueSubscription?.unsubscribe();
    this._resetForm();
    this._enableAllControls();
  }

  onEditEntity(item: any): void {
    this.isEditing = true;
    this.editingEntity = item;
    this.isAddParent = false;
    this.codeValueSubscription?.unsubscribe();
    this.parentCodeForSubAccount = '';
    this._resetForm();
    this._enableAllControls();

    this.loadTypeComptes().subscribe(() => {
      this.entityForm.patchValue({
        intituler: item.intitule,
        typeCompte: item.typeCompte,
        solde: item.solde,
        code: item.code,
        applicationId: this.appService.getAppId()!
      });

      if (item.parent && item.parent.id) {
        const parentCode = item.parent.code || item.parent.intitule;
        this.entityForm.patchValue({
          parentDisplay: `${parentCode}.${item.code}`,
          parentId: item.parent.id
        });
      } else {
        this.entityForm.patchValue({ parentDisplay: '', parentId: '' });
      }

      this.entityForm.get('parentDisplay')?.disable();
      this.entityForm.get('parentId')?.disable();
      this.showAddEntityModal = true;
    });
  }

  openDeleteConfirm(item: any): void {
    this.selectedItem = item;
    this.showDeleteConfirm = true;
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
    this.selectedItem = null;
  }

  confirmDelete(): void {
    if (!this.selectedItem) return;
    this.deleteLoading = true;
    this.entityService.delete(this.selectedItem.id).subscribe({
      next: () => { this.loadEntities(); this.deleteLoading = false; this.cancelDelete(); },
      error: (e) => { console.error(e); this.deleteLoading = false; }
    });
  }

  private _resetForm(): void {
    this.entityForm.reset({
      applicationId: this.appService.getAppId()!,
      code: '', intituler: '', typeCompte: '',
      solde: '', parentDisplay: '', parentId: ''
    });
  }

  private _enableAllControls(): void {
    ['code', 'intituler', 'typeCompte', 'solde', 'parentDisplay', 'parentId']
      .forEach(ctrl => this.entityForm.get(ctrl)?.enable());
  }
}